import React from "react";
import {
    X,
    MapPin,
    BriefcaseBusiness,
    Users,
    Clock3,
    CalendarDays,
    IndianRupee,
    Building2,
    Mail,
    Phone,
    Globe,
    UserRound,
    FileText,
} from "lucide-react";

const formatDate = (date) => {
    if (!date) return "Not specified";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
        return "Not specified";
    }

    return parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
};

const formatSalary = (min, max) => {
    const minimum = Number(min);
    const maximum = Number(max);

    const formatAmount = (amount) => {
        if (!Number.isFinite(amount)) return "";

        if (amount >= 100000) {
            return `₹${(amount / 100000)
                .toFixed(1)
                .replace(".0", "")} LPA`;
        }

        return `₹${new Intl.NumberFormat("en-IN").format(
            amount
        )}`;
    };

    if (minimum && maximum) {
        return `${formatAmount(minimum)} – ${formatAmount(
            maximum
        )}`;
    }

    if (minimum) return `From ${formatAmount(minimum)}`;

    if (maximum) return `Up to ${formatAmount(maximum)}`;

    return "Salary not disclosed";
};

const getSkills = (skills) => {
    if (!skills) return [];

    if (Array.isArray(skills)) {
        return skills
            .flatMap((item) => String(item).split(","))
            .map((item) => item.trim())
            .filter(Boolean);
    }

    return String(skills)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
};

const JobDetailModal = ({ job, onClose }) => {
    if (!job) return null;

    const partner = job.partner || {};

    const companyName =
        job.companyDisplayName ||
        partner.companyName ||
        job.companyName ||
        "Partner Company";

    const companyLocation =
        job.companyLocation ||
        job.location ||
        [partner.city, partner.state]
            .filter(Boolean)
            .join(", ") ||
        "India";

    const skills = getSkills(
        job.parsedSkills || job.skillsRequired
    );

    const status = String(
        job.status || "PENDING"
    ).toUpperCase();

    return (
        <div
            className="job-modal-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="job-modal">
                {/* Header */}
                <div className="job-modal-header">
                    <div className="job-modal-company">
                        <div className="modal-company-logo">
                            <Building2 size={28} />
                        </div>

                        <div>
                            <span>HIRING PARTNER</span>
                            <h2>{companyName}</h2>
                        </div>
                    </div>

                    <button
                        className="modal-close-btn"
                        onClick={onClose}
                    >
                        <X size={21} />
                    </button>
                </div>

                {/* Main title */}
                <div className="job-modal-title">
                    <div>
                        <span
                            className={`modal-status modal-status-${status.toLowerCase()}`}
                        >
                            {status}
                        </span>

                        <h1>
                            {job.jobTitle ||
                                "Job Opportunity"}
                        </h1>

                        <div className="modal-location">
                            <MapPin size={16} />
                            {companyLocation}
                        </div>
                    </div>
                </div>

                {/* Quick information */}
                <div className="modal-info-grid">
                    <div className="modal-info-box">
                        <BriefcaseBusiness size={18} />
                        <span>Experience</span>
                        <strong>
                            {job.experienceRequired ||
                                "Not specified"}
                        </strong>
                    </div>

                    <div className="modal-info-box">
                        <Clock3 size={18} />
                        <span>Employment</span>
                        <strong>
                            {job.employeeType ||
                                "Not specified"}
                        </strong>
                    </div>

                    <div className="modal-info-box">
                        <Users size={18} />
                        <span>Vacancies</span>
                        <strong>
                            {job.vacancy || 0}
                        </strong>
                    </div>

                    <div className="modal-info-box">
                        <IndianRupee size={18} />
                        <span>Salary</span>
                        <strong>
                            {formatSalary(
                                job.salaryMin,
                                job.salaryMax
                            )}
                        </strong>
                    </div>
                </div>

                <div className="job-modal-body">
                    {/* LEFT */}
                    <div className="modal-main-column">
                        <section className="modal-section">
                            <div className="modal-section-heading">
                                <FileText size={18} />
                                <h3>Job Description</h3>
                            </div>

                            <p className="modal-description">
                                {job.jobDescription ||
                                    "No job description has been provided."}
                            </p>
                        </section>

                        <section className="modal-section">
                            <div className="modal-section-heading">
                                <BriefcaseBusiness size={18} />
                                <h3>Required Skills</h3>
                            </div>

                            <div className="modal-skills">
                                {skills.length > 0 ? (
                                    skills.map(
                                        (skill, index) => (
                                            <span
                                                key={`${skill}-${index}`}
                                            >
                                                {skill}
                                            </span>
                                        )
                                    )
                                ) : (
                                    <span>
                                        Skills not specified
                                    </span>
                                )}
                            </div>
                        </section>

                        <section className="modal-section">
                            <div className="modal-section-heading">
                                <CalendarDays size={18} />
                                <h3>Application Timeline</h3>
                            </div>

                            <div className="timeline-box">
                                <div>
                                    <span>
                                        Posted On
                                    </span>
                                    <strong>
                                        {formatDate(
                                            job.createdAt
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Application Deadline
                                    </span>
                                    <strong>
                                        {formatDate(
                                            job.applicationDeadline
                                        )}
                                    </strong>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* RIGHT */}
                    <aside className="modal-company-sidebar">
                        <div className="company-sidebar-heading">
                            <Building2 size={18} />
                            <h3>Company Details</h3>
                        </div>

                        <div className="company-detail-item">
                            <Building2 size={16} />
                            <div>
                                <span>Company</span>
                                <strong>
                                    {companyName}
                                </strong>
                            </div>
                        </div>

                        {partner.companyType && (
                            <div className="company-detail-item">
                                <BriefcaseBusiness size={16} />
                                <div>
                                    <span>
                                        Company Type
                                    </span>
                                    <strong>
                                        {
                                            partner.companyType
                                        }
                                    </strong>
                                </div>
                            </div>
                        )}

                        {partner.name && (
                            <div className="company-detail-item">
                                <UserRound size={16} />
                                <div>
                                    <span>
                                        Contact Person
                                    </span>
                                    <strong>
                                        {partner.name}
                                    </strong>

                                    {partner.designation && (
                                        <small>
                                            {
                                                partner.designation
                                            }
                                        </small>
                                    )}
                                </div>
                            </div>
                        )}

                        {partner.email && (
                            <div className="company-detail-item">
                                <Mail size={16} />
                                <div>
                                    <span>Email</span>
                                    <strong>
                                        {partner.email}
                                    </strong>
                                </div>
                            </div>
                        )}

                        {partner.mobile && (
                            <div className="company-detail-item">
                                <Phone size={16} />
                                <div>
                                    <span>Phone</span>
                                    <strong>
                                        {partner.mobile}
                                    </strong>
                                </div>
                            </div>
                        )}

                        {partner.website && (
                            <div className="company-detail-item">
                                <Globe size={16} />
                                <div>
                                    <span>Website</span>
                                    <a
                                        href={
                                            partner.website.startsWith(
                                                "http"
                                            )
                                                ? partner.website
                                                : `https://${partner.website}`
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Visit Website
                                    </a>
                                </div>
                            </div>
                        )}

                        <div className="company-detail-item">
                            <MapPin size={16} />
                            <div>
                                <span>Location</span>
                                <strong>
                                    {companyLocation}
                                </strong>

                                {partner.address && (
                                    <small>
                                        {partner.address}
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="modal-apply-box">
                            <span>Interested in this role?</span>

                            <button
                                onClick={() => {
                                    alert(
                                        "Application flow can be connected here."
                                    );
                                }}
                            >
                                Apply for this Job
                            </button>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default JobDetailModal;